// Wrappers de fetch para los endpoints /api/admin/*.
// Usan el token Bearer guardado por AuthContext (idoyoga_auth_token).
//
// Todos los endpoints aquí asumen que el usuario logado es admin; si no,
// el backend responde 403 ROL_INSUFICIENTE y la guard del frontend ya
// debería haberlo desviado.

import { API_BASE, ApiError } from './api';

const TOKEN_KEY = 'idoyoga_auth_token';

function token() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

async function adminFetch(path, opts = {}) {
  const headers = {
    Authorization: `Bearer ${token() || ''}`,
    ...(opts.headers || {}),
  };
  // Si el body es FormData NO seteamos Content-Type — el browser añade
  // automáticamente 'multipart/form-data; boundary=...'.
  const esFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body && !headers['Content-Type'] && !esFormData) {
    headers['Content-Type'] = 'application/json';
  }
  const r = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  let body = null;
  try { body = await r.json(); } catch (_) {}
  if (!r.ok) throw new ApiError(r.status, body?.code, body?.error);
  return body;
}

// --- Dashboard ---
export const getDashboard = () => adminFetch('/api/admin/dashboard');

// --- Horarios ---
export const listHorarios       = () => adminFetch('/api/admin/horarios');
export const getHorario         = (id) => adminFetch(`/api/admin/horarios/${id}`);
export const createHorario      = (body) => adminFetch('/api/admin/horarios', { method: 'POST', body: JSON.stringify(body) });
export const updateHorario      = (id, body) => adminFetch(`/api/admin/horarios/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteHorario      = (id) => adminFetch(`/api/admin/horarios/${id}`, { method: 'DELETE' });

// --- Profesores ---
export const listProfesores     = () => adminFetch('/api/admin/profesores');
export const getProfesorAdmin   = (id) => adminFetch(`/api/admin/profesores/${id}`);
export const createProfesor     = (body) => adminFetch('/api/admin/profesores', { method: 'POST', body: JSON.stringify(body) });
export const updateProfesor     = (id, body) => adminFetch(`/api/admin/profesores/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteProfesor     = (id, definitivo = false) => adminFetch(`/api/admin/profesores/${id}${definitivo ? '?definitivo=1' : ''}`, { method: 'DELETE' });
export const getProfesorDatosFiscales = (id) => adminFetch(`/api/admin/profesores/${id}/datos-fiscales`);
export const putProfesorDatosFiscales = (id, body) => adminFetch(`/api/admin/profesores/${id}/datos-fiscales`, {
  method: 'PUT',
  body: JSON.stringify(body),
});
// SS7.8.bucket — Foto profesor
export const subirFotoProfesor = (id, file) => {
  const fd = new FormData();
  fd.append('foto', file);
  return adminFetch(`/api/admin/profesores/${id}/foto`, { method: 'POST', body: fd });
};
export const eliminarFotoProfesor = (id) =>
  adminFetch(`/api/admin/profesores/${id}/foto`, { method: 'DELETE' });

// SS-EVT — Eventos
export const listEventosAdmin   = () => adminFetch('/api/admin/eventos');
export const getEventoAdmin     = (id) => adminFetch(`/api/admin/eventos/${id}`);
export const createEvento       = (body) => adminFetch('/api/admin/eventos', { method: 'POST', body: JSON.stringify(body) });
export const updateEvento       = (id, body) => adminFetch(`/api/admin/eventos/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteEvento       = (id) => adminFetch(`/api/admin/eventos/${id}`, { method: 'DELETE' });
export const listEventoInscripciones = (id) => adminFetch(`/api/admin/eventos/${id}/inscripciones`);
export const subirFotoEvento    = (id, file) => {
  const fd = new FormData();
  fd.append('foto', file);
  // Los carteles ya llevan su logo: se sube el original, sin marca de agua ni filtro.
  fd.append('sin_tratamiento', '1');
  return adminFetch(`/api/admin/eventos/${id}/foto`, { method: 'POST', body: fd });
};
export const leerCartelEvento = (file) => {
  const fd = new FormData();
  fd.append('cartel', file);
  return adminFetch('/api/admin/eventos/leer-cartel', { method: 'POST', body: fd });
};
export const eliminarFotoEvento = (id) =>
  adminFetch(`/api/admin/eventos/${id}/foto`, { method: 'DELETE' });

// SS-EVT-IA — Generación foto con DALL·E 3
export const getSugerenciaPromptIA = (id) =>
  adminFetch(`/api/admin/eventos/${id}/sugerencia-prompt-ia`);
export const generarFotoIA = (id, body) =>
  adminFetch(`/api/admin/eventos/${id}/generar-foto-ia`, {
    method: 'POST', body: JSON.stringify(body || {}),
  });

// SS-EVT-PARTICIPANTES — co-facilitadores
export const listEventoParticipantes = (eventoId) =>
  adminFetch(`/api/admin/eventos/${eventoId}/participantes`);
export const createEventoParticipante = (eventoId, body) =>
  adminFetch(`/api/admin/eventos/${eventoId}/participantes`, {
    method: 'POST', body: JSON.stringify(body),
  });
export const updateEventoParticipante = (eventoId, partId, body) =>
  adminFetch(`/api/admin/eventos/${eventoId}/participantes/${partId}`, {
    method: 'PUT', body: JSON.stringify(body),
  });
export const deleteEventoParticipante = (eventoId, partId) =>
  adminFetch(`/api/admin/eventos/${eventoId}/participantes/${partId}`, { method: 'DELETE' });
export const reordenarParticipantes = (eventoId, orden) =>
  adminFetch(`/api/admin/eventos/${eventoId}/participantes/orden`, {
    method: 'PUT', body: JSON.stringify({ orden }),
  });
export const subirFotoParticipante = (eventoId, partId, file) => {
  const fd = new FormData();
  fd.append('foto', file);
  return adminFetch(`/api/admin/eventos/${eventoId}/participantes/${partId}/foto`, {
    method: 'POST', body: fd,
  });
};
export const eliminarFotoParticipante = (eventoId, partId) =>
  adminFetch(`/api/admin/eventos/${eventoId}/participantes/${partId}/foto`, { method: 'DELETE' });

// SS-REDES — Instagram config + sync
export const getRedesConfig = () => adminFetch('/api/admin/redes');
export const updateRedesConfig = (body) => adminFetch('/api/admin/redes', {
  method: 'PUT', body: JSON.stringify(body),
});
export const probarRedesConexion = () => adminFetch('/api/admin/redes/probar', { method: 'POST' });
export const sincronizarRedes = () => adminFetch('/api/admin/redes/sincronizar', { method: 'POST' });
export const refrescarRedesToken = () => adminFetch('/api/admin/redes/refrescar', { method: 'POST' });
export const getProfesorDescuentos = (id) => adminFetch(`/api/admin/profesores/${id}/descuentos`);
export const putProfesorDescuentos = (id, body) => adminFetch(`/api/admin/profesores/${id}/descuentos`, {
  method: 'PUT',
  body: JSON.stringify(body),
});

// --- Sesiones (asignación profesor) ---
export const listSesiones = (filtros = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/sesiones?${p.toString()}`);
};
export const asignarProfesorSesion = (sesionId, body) => adminFetch(`/api/admin/sesiones/${sesionId}/asignar`, {
  method: 'POST',
  body: JSON.stringify(body),
});
export const desasignarProfesorSesion = (sesionId) => adminFetch(`/api/admin/sesiones/${sesionId}/asignar`, {
  method: 'DELETE',
});

// --- Configuración emails profesor ---
export const getConfigEmailsProfesor = () => adminFetch('/api/admin/configuracion-emails-profesor');
export const putConfigEmailsProfesor = (body) => adminFetch('/api/admin/configuracion-emails-profesor', {
  method: 'PUT',
  body: JSON.stringify(body),
});

// --- Configuración estudio (datos fiscales) ---
export const getConfigEstudio = () => adminFetch('/api/admin/configuracion-estudio');
export const putConfigEstudio = (body) => adminFetch('/api/admin/configuracion-estudio', {
  method: 'PUT',
  body: JSON.stringify(body),
});

// --- Clases tipos ---
export const listClasesTipos    = () => adminFetch('/api/admin/clases-tipos');
export const createClaseTipo    = (body) => adminFetch('/api/admin/clases-tipos', { method: 'POST', body: JSON.stringify(body) });
export const updateClaseTipo    = (id, body) => adminFetch(`/api/admin/clases-tipos/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteClaseTipo    = (id) => adminFetch(`/api/admin/clases-tipos/${id}`, { method: 'DELETE' });

// --- Bonos comprados (vendidos a alumnos) ---
export const listBonosComprados = (filtros = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/bonos-comprados?${p.toString()}`);
};
export const createBonoComprado = (body) => adminFetch('/api/admin/bonos-comprados', {
  method: 'POST',
  body: JSON.stringify(body),
});
export const updateBonoComprado = (id, body) => adminFetch(`/api/admin/bonos-comprados/${id}`, {
  method: 'PUT',
  body: JSON.stringify(body),
});
export const cancelarBonoComprado = (id) => adminFetch(`/api/admin/bonos-comprados/${id}`, {
  method: 'DELETE',
});

// --- Bonos tipos ---
export const listBonosTipos     = () => adminFetch('/api/admin/bonos-tipos');
export const createBonoTipo     = (body) => adminFetch('/api/admin/bonos-tipos', { method: 'POST', body: JSON.stringify(body) });
export const updateBonoTipo     = (id, body) => adminFetch(`/api/admin/bonos-tipos/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteBonoTipo     = (id) => adminFetch(`/api/admin/bonos-tipos/${id}`, { method: 'DELETE' });

// --- Profesor tarifas ---
export const listProfesorTarifas = (filtros = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/profesor-tarifas?${p.toString()}`);
};
export const createProfesorTarifa = (body) => adminFetch('/api/admin/profesor-tarifas', {
  method: 'POST',
  body: JSON.stringify(body),
});
export const updateProfesorTarifa = (id, body) => adminFetch(`/api/admin/profesor-tarifas/${id}`, {
  method: 'PUT',
  body: JSON.stringify(body),
});
export const deleteProfesorTarifa = (id) => adminFetch(`/api/admin/profesor-tarifas/${id}`, {
  method: 'DELETE',
});

// --- Pagos profesor ---
export const listPagosProfesor = (filtros = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/pagos-profesor?${p.toString()}`);
};
export const marcarPagadoProfesor = (body) => adminFetch('/api/admin/pagos-profesor/marcar-pagado', {
  method: 'POST',
  body: JSON.stringify(body),
});
export const anularPagoProfesor = (id) => adminFetch(`/api/admin/pagos-profesor/${id}/anular`, {
  method: 'POST',
});
export const marcarPagoPendiente = (id) => adminFetch(`/api/admin/pagos-profesor/${id}/marcar-pendiente`, {
  method: 'POST',
});

// Descarga binaria (PDF). Usa Bearer; al recibir el blob, dispara descarga
// programática con anchor + download attribute. Si el endpoint devuelve error
// JSON (no PDF), parsea y lanza ApiError.
export const descargarReciboProfesor = async (profesorId, periodo) => {
  const r = await fetch(`${API_BASE}/api/admin/pagos-profesor/recibo/${profesorId}?periodo=${encodeURIComponent(periodo)}`, {
    headers: { Authorization: `Bearer ${token() || ''}` },
  });
  if (!r.ok) {
    let body = null;
    try { body = await r.json(); } catch (_) {}
    throw new ApiError(r.status, body?.code, body?.error);
  }
  const blob = await r.blob();
  const filename = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)/)?.[1] || `recibo_${periodo}.pdf`;
  _triggerDownload(blob, filename);
};

export const descargarModelo111 = async (trimestre) => {
  const r = await fetch(`${API_BASE}/api/admin/pagos-profesor/modelo-111?trimestre=${encodeURIComponent(trimestre)}`, {
    headers: { Authorization: `Bearer ${token() || ''}` },
  });
  if (!r.ok) {
    let body = null;
    try { body = await r.json(); } catch (_) {}
    throw new ApiError(r.status, body?.code, body?.error);
  }
  const blob = await r.blob();
  const filename = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)/)?.[1] || `modelo_111_${trimestre}.pdf`;
  _triggerDownload(blob, filename);
};

function _triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

// --- Reservas ---
export const listReservas = (filtros = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/reservas?${p.toString()}`);
};
export const getReservaAdmin    = (codigo) => adminFetch(`/api/admin/reservas/${codigo}`);
export const cancelarReservaAdmin = (codigo) => adminFetch(`/api/admin/reservas/${codigo}/cancelar`, { method: 'POST' });
export const eliminarReservaAdmin = (codigo) => adminFetch(`/api/admin/reservas/${codigo}`, { method: 'DELETE' });
export const marcarPagada       = (codigo) => adminFetch(`/api/admin/reservas/${codigo}/marcar-pagada`, { method: 'POST' });
export const marcarAsistencia   = (codigo, estado) => adminFetch(`/api/admin/reservas/${codigo}/marcar-asistencia`, {
  method: 'POST',
  body: JSON.stringify({ estado }),
});

// --- Alumnos top (ranking por asistencias) ---
export const getAlumnosTop = (params = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/alumnos-top?${p.toString()}`);
};

// --- Alumnos inactivos (con bono activo + sin asistir N días) ---
export const getAlumnosInactivos = (params = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/alumnos-inactivos?${p.toString()}`);
};

// --- SS7.2.6: gestión de usuarios admin (super-admin only) ---
export const listUsuariosAdmin = (params = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/usuarios-admin?${p.toString()}`);
};
export const invitarAdmin = (email) => adminFetch('/api/admin/usuarios-admin/invitar', {
  method: 'POST', body: JSON.stringify({ email }),
});
export const listInvitacionesAdmin = (params = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/usuarios-admin/invitaciones?${p.toString()}`);
};
export const revocarInvitacion = (id) => adminFetch(`/api/admin/usuarios-admin/invitaciones/${id}`, {
  method: 'DELETE',
});
export const desactivarAdminUser = (id) => adminFetch(`/api/admin/usuarios-admin/${id}/desactivar`, {
  method: 'PUT',
});
export const reactivarAdminUser = (id) => adminFetch(`/api/admin/usuarios-admin/${id}/reactivar`, {
  method: 'PUT',
});
export const updateAdminUser = (id, body) => adminFetch(`/api/admin/usuarios-admin/${id}`, {
  method: 'PUT', body: JSON.stringify(body),
});
export const getAdminAuditLog = (params = {}) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  }
  return adminFetch(`/api/admin/usuarios-admin/audit-log?${p.toString()}`);
};

// Endpoints públicos de activación admin (no requieren auth).
export const getActivarAdminInfo = async (token) => {
  const r = await fetch(`${API_BASE}/api/auth/activar-admin/${encodeURIComponent(token)}`);
  let body = null;
  try { body = await r.json(); } catch (_) {}
  if (!r.ok) throw new ApiError(r.status, body?.code, body?.error);
  return body;
};
export const postActivarAdmin = async (token, payload) => {
  const r = await fetch(`${API_BASE}/api/auth/activar-admin/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let body = null;
  try { body = await r.json(); } catch (_) {}
  if (!r.ok) throw new ApiError(r.status, body?.code, body?.error);
  return body;
};

// --- mejora-web F4: galería (portada + eventos) ---
export const listGaleria = (ambito = 'portada', eventoId = null) =>
  adminFetch(`/api/admin/galeria?ambito=${ambito}${eventoId ? `&evento_id=${eventoId}` : ''}`);
export const subirFotoGaleria = (file, { ambito = 'portada', eventoId = null, sinTratamiento = false } = {}) => {
  const fd = new FormData();
  fd.append('foto', file);
  fd.append('ambito', ambito);
  if (eventoId) fd.append('evento_id', eventoId);
  // Por defecto el backend aplica el filtro de la web y la marca de agua.
  if (sinTratamiento) fd.append('sin_tratamiento', '1');
  return adminFetch('/api/admin/galeria', { method: 'POST', body: fd });
};
export const updateFotoGaleria = (id, body) => adminFetch(`/api/admin/galeria/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const ordenarGaleria    = (ids) => adminFetch('/api/admin/galeria/orden', { method: 'PUT', body: JSON.stringify({ ids }) });
export const deleteFotoGaleria = (id) => adminFetch(`/api/admin/galeria/${id}`, { method: 'DELETE' });
// «Vídeo del día»: saca un fotograma del vídeo (portada), sube el vídeo directo a Storage y da de alta la fila.
function fotogramaDeVideo(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.muted = true; v.playsInline = true; v.preload = 'metadata'; v.src = url;
    const fin = (fn, arg) => { URL.revokeObjectURL(url); fn(arg); };
    v.onerror = () => fin(reject, new ApiError(0, 'VIDEO_ILEGIBLE', 'No se pudo leer el vídeo en este navegador.'));
    v.onloadedmetadata = () => { v.currentTime = Math.min(1, (v.duration || 2) / 4); };
    v.onseeked = () => {
      const escala = Math.min(1, 1280 / (v.videoWidth || 1280));
      const c = document.createElement('canvas');
      c.width = Math.round(v.videoWidth * escala); c.height = Math.round(v.videoHeight * escala);
      c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((b) => (b ? fin(resolve, new File([b], 'portada.jpg', { type: 'image/jpeg' })) : fin(reject, new ApiError(0, 'VIDEO_ILEGIBLE', 'No se pudo sacar la portada del vídeo.'))), 'image/jpeg', 0.9);
    };
  });
}
export async function subirVideoGaleria(file, { sinTratamiento = false } = {}, onProgreso) {
  const portada = await fotogramaDeVideo(file);
  const prep = await adminFetch('/api/admin/galeria/video-subida-url', {
    method: 'POST', body: JSON.stringify({ mimetype: file.type, size: file.size }),
  });
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', prep.signed_url);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgreso) onProgreso(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve()
      : reject(new ApiError(xhr.status, 'STORAGE_ERROR', `Error al subir el vídeo (HTTP ${xhr.status}).`)));
    xhr.onerror = () => reject(new ApiError(0, 'STORAGE_ERROR', 'Error de red al subir el vídeo.'));
    const fd = new FormData();
    fd.append('cacheControl', '3600');
    fd.append('', file);
    xhr.send(fd);
  });
  const fd = new FormData();
  fd.append('foto', portada);
  fd.append('ambito', 'video_diario');
  fd.append('video_path', prep.path);
  if (sinTratamiento) fd.append('sin_tratamiento', '1');
  return adminFetch('/api/admin/galeria', { method: 'POST', body: fd });
}
export const generarFraseGaleria = (id) => adminFetch(`/api/admin/galeria/${id}/frase-ia`, { method: 'POST' });

// --- mejora-web F4: vídeos ---
export const listVideosAdmin = () => adminFetch('/api/admin/videos');
export const createVideo     = (body) => adminFetch('/api/admin/videos', { method: 'POST', body: JSON.stringify(body) });
export const updateVideo     = (id, body) => adminFetch(`/api/admin/videos/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteVideo     = (id) => adminFetch(`/api/admin/videos/${id}`, { method: 'DELETE' });
export const subirPortadaVideo = (id, file) => {
  const fd = new FormData();
  fd.append('foto', file);
  return adminFetch(`/api/admin/videos/${id}/portada`, { method: 'POST', body: fd });
};
// Sube el fichero de vídeo directamente a Supabase Storage (URL firmada) con
// progreso. Devuelve el storage_path para guardarlo en el vídeo.
export async function subirFicheroVideo(file, onProgreso) {
  const prep = await adminFetch('/api/admin/videos/subida-url', {
    method: 'POST',
    body: JSON.stringify({ nombre: file.name, mimetype: file.type, size: file.size }),
  });
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', prep.signed_url);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgreso) onProgreso(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new ApiError(xhr.status, 'STORAGE_ERROR', `Error al subir el vídeo (HTTP ${xhr.status}).`)));
    xhr.onerror = () => reject(new ApiError(0, 'STORAGE_ERROR', 'Error de red al subir el vídeo.'));
    const fd = new FormData();
    fd.append('cacheControl', '3600');
    fd.append('', file);
    xhr.send(fd);
  });
  return prep.path;
}

// --- mejora-web F4: promociones ---
export const listPromociones   = () => adminFetch('/api/admin/promociones');
export const createPromocion   = (body) => adminFetch('/api/admin/promociones', { method: 'POST', body: JSON.stringify(body) });
export const updatePromocion   = (id, body) => adminFetch(`/api/admin/promociones/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deletePromocion   = (id) => adminFetch(`/api/admin/promociones/${id}`, { method: 'DELETE' });

// --- mejora-web F4: textos de portada ---
export const getContenidoPortada = () => adminFetch('/api/admin/contenido-portada');
export const putContenidoPortada = (textos) => adminFetch('/api/admin/contenido-portada', { method: 'PUT', body: JSON.stringify({ textos }) });

// --- Códigos de descuento / tarjetas regalo ---
export const listCodigos      = () => adminFetch('/api/admin/codigos-descuento');
export const createCodigo     = (body) => adminFetch('/api/admin/codigos-descuento', { method: 'POST', body: JSON.stringify(body) });
export const updateCodigo     = (id, body) => adminFetch(`/api/admin/codigos-descuento/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteCodigo     = (id) => adminFetch(`/api/admin/codigos-descuento/${id}`, { method: 'DELETE' });
export const enviarCodigoEmail = (id, body) => adminFetch(`/api/admin/codigos-descuento/${id}/enviar`, { method: 'POST', body: JSON.stringify(body) });
export const listUsosCodigo   = (id) => adminFetch(`/api/admin/codigos-descuento/${id}/usos`);

// --- Tarjetas regalo de empresa: tramos de descuento por volumen ---
export const listTramosEmpresa      = () => adminFetch('/api/admin/tramos-descuento-empresa');
export const createTramoEmpresa     = (body) => adminFetch('/api/admin/tramos-descuento-empresa', { method: 'POST', body: JSON.stringify(body) });
export const updateTramoEmpresa     = (id, body) => adminFetch(`/api/admin/tramos-descuento-empresa/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteTramoEmpresa     = (id) => adminFetch(`/api/admin/tramos-descuento-empresa/${id}`, { method: 'DELETE' });
export const simularDescuentoEmpresa = (valor) => adminFetch(`/api/admin/tramos-descuento-empresa/simular?valor=${encodeURIComponent(valor)}`);
export const listPedidosEmpresa = () => adminFetch('/api/admin/pedidos-empresa');

export const traducirTexto = (texto, origen) => adminFetch('/api/admin/contenido-portada/traducir', { method: 'POST', body: JSON.stringify({ texto, origen }) });

export const listSolicitudesClase = () => adminFetch('/api/admin/solicitudes-clase');
export const confirmarSolicitudClase = (id, body) => adminFetch(`/api/admin/solicitudes-clase/${id}/confirmar`, { method: 'POST', body: JSON.stringify(body) });
export const rechazarSolicitudClase = (id, nota) => adminFetch(`/api/admin/solicitudes-clase/${id}/rechazar`, { method: 'POST', body: JSON.stringify({ nota }) });

export const cancelarInscripcionEventoAdmin = (eventoId, insId) => adminFetch(`/api/admin/eventos/${eventoId}/inscripciones/${insId}/cancelar`, { method: 'POST' });
export const cancelarSolicitudClasePagada = (id) => adminFetch(`/api/admin/solicitudes-clase/${id}/cancelar`, { method: 'POST' });
